import { supabase } from '../../lib/supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';

export interface SignUpData {
    email: string;
    password: string;
}

export interface SignInData {
    email: string;
    password: string;
}

/**
 * API service for Authentication operations
 */
export const authApi = {
    /**
     * Sign up a new user
     */
    async signUp(
        credentials: SignUpData
    ): Promise<{ data: { user: User | null; session: Session | null }; error: AuthError | null }> {
        const { data, error } = await supabase.auth.signUp(credentials);
        return { data, error };
    },

    /**
     * Sign in an existing user
     */
    async signIn(
        credentials: SignInData
    ): Promise<{ data: { user: User | null; session: Session | null }; error: AuthError | null }> {
        const { data, error } = await supabase.auth.signInWithPassword(credentials);
        return { data, error };
    },

    /**
     * Sign out the current user
     */
    async signOut(): Promise<{ error: AuthError | null }> {
        const { error } = await supabase.auth.signOut();
        return { error };
    },

    /**
     * Get the current session
     */
    async getSession(): Promise<{ data: { session: Session | null }; error: AuthError | null }> {
        const { data, error } = await supabase.auth.getSession();
        return { data, error };
    },

    /**
     * Get the current user
     */
    async getUser(): Promise<{ data: { user: User | null }; error: AuthError | null }> {
        const { data, error } = await supabase.auth.getUser();
        return { data, error };
    },

    /**
     * Listen to auth state changes
     */
    onAuthStateChange(callback: (event: string, session: Session | null) => void) {
        return supabase.auth.onAuthStateChange((event, session) => {
            callback(event, session);
        });
    },

    /**
     * Reset password
     */
    async resetPassword(email: string): Promise<{ error: AuthError | null }> {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        return { error };
    },

    /**
     * Update user password
     */
    async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
        const { error } = await supabase.auth.updateUser({
            password: newPassword,
        });
        return { error };
    },
};

